import type { InfiniteData, Query, QueryClient } from "@tanstack/react-query";
import { SPATIAL_DATASETS_TABLE, TABULAR_DATASETS_TABLE } from "../utilities/configAdapter";
import type { AssetStatus } from "./assetStatus";

/** The drawer and entity caches are keyed by tableName: datasets live under two, media under `media`. */
const ASSET_TABLES = {
  datasets: new Set<unknown>([SPATIAL_DATASETS_TABLE, TABULAR_DATASETS_TABLE]),
  media: new Set<unknown>(["media"])
};

export type AssetTables = keyof typeof ASSET_TABLES;

const isAssetQuery = (tables: Set<unknown>, root: string, id?: string) => (query: Query) =>
  query.queryKey[0] === root &&
  (id === undefined ? tables.has(query.queryKey[1]) : query.queryKey[1] === id && tables.has(query.queryKey[2]));

/**
 * Writes an asset's new status into every cache that shows it, so the drawer
 * tile and an open detail card never disagree, and a live status re-arms
 * their polls.
 *
 * The drawer list holds raw API rows; the entity cache holds an adapted item
 * with the asset fields on it (what the detail card reads) and again under
 * `_source.rawData`. All are patched. Only the given tables are touched: a
 * dataset, a media item and a CM row can share an id.
 *
 * Fetches of those caches already in flight are cancelled first. One that
 * started before the server recorded the new status would land after this write
 * and put the old one back; an "uploading" row isn't polled, so it would stay
 * stuck there. Once the new status is written, the list's poll takes over.
 */
export const applyAssetStatus = async (
  queryClient: QueryClient,
  id: number | string,
  processing_status: AssetStatus,
  tables: AssetTables = "datasets"
): Promise<void> => {
  const inTables = ASSET_TABLES[tables];
  const itemId = String(id);
  const lists = { predicate: isAssetQuery(inTables, "drawer-data") };
  const details = { predicate: isAssetQuery(inTables, "entity", itemId) };
  // A stale reason must not survive a new attempt.
  const patch = { processing_status, processing_error: "" };

  await Promise.all([queryClient.cancelQueries(lists), queryClient.cancelQueries(details)]);

  queryClient.setQueriesData<InfiniteData<any>>(lists, (oldData) => {
    if (!oldData?.pages) return oldData;
    let found = false;
    const pages = oldData.pages.map((page) => {
      if (!Array.isArray(page?.data)) return page;
      const index = page.data.findIndex((row: any) => String(row?.id) === itemId);
      if (index === -1) return page;
      found = true;
      const data = [...page.data];
      data[index] = { ...data[index], ...patch };
      return { ...page, data };
    });
    return found ? { ...oldData, pages } : oldData;
  });

  queryClient.setQueriesData(details, (oldData: any) => {
    if (!oldData) return oldData;
    const next = { ...oldData, ...patch };
    if (oldData._source?.rawData) next._source = { ...oldData._source, rawData: { ...oldData._source.rawData, ...patch } };
    return next;
  });
};
